import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export const formatDate = (date) => {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export const formatDateTime = (date) => {
  return new Date(date).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const parseReviewerList = (list = []) => {
  return list?.map((item) => item?.value?.name).filter(Boolean);
};

export const toApprovalsRows = (arr, usersMap = {}) =>
  arr?.map((item, i) => {
    const sub = typeof item === 'string' ? item : item.sub;
    const name = typeof item === 'string' ? item : item.name;
    const user = usersMap[sub];
    const designation = user?.designation || '';
    return {
      id: Date.now() + i,
      order: i,
      value: { sub, name, designation },
    };
  });

/** Categories arrive either as an array or as a ';'-separated string. */
export const getCategoryItems = (categories) => {
  if (Array.isArray(categories)) return categories.filter(Boolean);
  if (typeof categories !== 'string') return [];

  return categories
    .split(';')
    .map((item) => item.trim())
    .filter(Boolean);
};

/** Nearest action-plan implementation date, or '-' when there is none. */
export const getClosestActionPlanImplementAt = (qualityItem) => {
  const now = Date.now();
  const closestPlan = qualityItem.planActions?.reduce((closest, plan) => {
    const planTime = Date.parse(plan.implementAt);

    if (Number.isNaN(planTime)) return closest;

    const planDiff = Math.abs(planTime - now);

    if (!closest || planDiff < closest.diff) {
      return { diff: planDiff, implementAt: plan.implementAt };
    }

    return closest;
  }, null);

  return closestPlan?.implementAt || '-';
};

/**
 * True when any field matches `query` as a case-insensitive substring, and
 * vacuously true for an empty query.
 *
 * Objects are skipped deliberately: passing a whole entity used to stringify
 * to "[object Object]", so every row matched any query containing "object".
 */
export const matchesFieldsSearch = (fields, query) => {
  const needle = String(query ?? '')
    .toLowerCase()
    .trim();

  if (!needle) return true;

  return fields.some((field) => {
    if (field == null || typeof field === 'object') return false;

    return String(field).toLowerCase().includes(needle);
  });
};

/**
 * Every value visible in the Documents table rows, so the search bar matches
 * what the user can actually see (Rev, Doc Type label and Effective Date
 * included). Keep in sync with DocumentsTableBody's <TableCell>s.
 */
export const getDocumentSearchFields = (doc, templateTypeMap = {}) => [
  doc.serial,
  doc.documentNumber,
  templateTypeMap[doc.templateId] || doc.templateId,
  doc.revision,
  doc.owner || doc.creatorName || doc.creator,
  doc.documentTitle,
  doc.releasedAt?.split('T')[0],
  doc.status,
];

export const matchesDocumentSearch = (doc, query, templateTypeMap) =>
  matchesFieldsSearch(getDocumentSearchFields(doc, templateTypeMap), query);

/**
 * Sort value for a Change Control column.
 *
 * Column keys that aren't a direct key on the row need unwrapping: sorting the
 * `document` object itself stringifies to "[object Object]", which compares
 * equal to every other row, so the column silently never moved.
 */
export const getControlSortValue = (control, sortBy) =>
  sortBy === 'documentTitle'
    ? (control.document?.title ?? '')
    : (control[sortBy] ?? '');

/**
 * Values visible in the Change Control table for the active view.
 *
 * Both views show the same columns except Requestor, which only the pending
 * (approval queue) view has.
 */
export const getControlSearchFields = (control, isMasterView) => [
  control.serial,
  control.document?.title || control.document?.id,
  ...(isMasterView ? [] : [control.requestorName || control.requestorId]),
  control.informationReason,
  control.status,
];

/**
 * Values visible in the Quality Event table for the active view.
 *
 * Both views show the same columns; Reported At and Reporter are the only
 * pending-only ones.
 *
 * `includeCategories` must track SHOW_MASTER_LIST_CATEGORIES in
 * QualityListPage: the Categories sub-row is behind that flag, so searching
 * them while it is off would match text the user cannot see.
 */
export const getQualitySearchFields = (
  quality,
  isMasterView,
  includeCategories = false
) => [
  quality.serial,
  quality.id,
  quality.nc ? 'Yes' : 'No',
  ...(isMasterView
    ? []
    : [quality.createdAt?.split('T')[0], quality.creatorName]),
  quality.informationDescription,
  ...(includeCategories ? getCategoryItems(quality.categories) : []),
  getClosestActionPlanImplementAt(quality),
  quality.status,
];

/**
 * Split `text` into segments marking every case-insensitive occurrence of
 * `query`, so the caller can wrap matches in <mark>. Plain substring matching
 * (no regex) keeps user-typed characters like "(" or "." literal.
 */
export const highlightSegments = (text, query) => {
  const str = String(text ?? '');
  const needle = String(query ?? '')
    .toLowerCase()
    .trim();

  if (!needle || !str) return [{ text: str, match: false }];

  const lower = str.toLowerCase();
  const segments = [];
  let i = 0;

  while (i < str.length) {
    const found = lower.indexOf(needle, i);

    if (found === -1) {
      segments.push({ text: str.slice(i), match: false });
      break;
    }

    if (found > i) segments.push({ text: str.slice(i, found), match: false });

    segments.push({
      text: str.slice(found, found + needle.length),
      match: true,
    });
    i = found + needle.length;
  }

  return segments;
};

// Statuses that belong in the "Other Pending" filters. Everything else is
// owned by another tab: RELEASED/ARCHIVED/CLOSED/PENDING_* live in the Master
// List, APPROVED has its own tab, and REJECTED is terminal (surfaced in the
// Master List behind the "include rejected" filter).
export const PENDING_STATUSES = ['DRAFT', 'REVIEWING', 'RETURNED'];

// Groups allowed to see terminal records (REJECTED, ARCHIVED) in the Master
// List, via the "Include" filter dropdown.
export const MASTER_LIST_FILTER_GROUPS = ['QA', 'PowerUser', 'SuperAdmin'];
